import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatPixTimeLeft,
  isPixOrderActive,
  pixExpiresInMs,
  type PixOrderSnapshot,
} from "../lib/pixCheckout.js";

export function ResumePixBanner({
  order,
  onResume,
  onExpired,
  loading = false,
  titleKey = "checkout.resumePixTitle",
  hintKey = "checkout.resumePixHint",
  hintNoTimerKey = "checkout.resumePixHintNoTimer",
  ctaKey = "checkout.resumePixCta",
}: {
  order: Pick<PixOrderSnapshot, "pix_expires_at">;
  onResume: () => void | Promise<void>;
  /** Called once when the countdown reaches zero — hide the banner. */
  onExpired?: () => void;
  loading?: boolean;
  titleKey?: string;
  hintKey?: string;
  hintNoTimerKey?: string;
  ctaKey?: string;
}) {
  const { t } = useTranslation();
  const [msLeft, setMsLeft] = useState<number | null>(() => pixExpiresInMs(order as PixOrderSnapshot));

  useEffect(() => {
    if (!order.pix_expires_at) return;
    const tick = () => {
      const next = pixExpiresInMs(order as PixOrderSnapshot);
      setMsLeft(next);
      if (next === 0) onExpired?.();
    };
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [order.pix_expires_at, onExpired]);

  if (!isPixOrderActive(order as PixOrderSnapshot)) return null;

  return (
    <div className="rounded-xl border border-purple-deep/25 bg-purple-deep/5 p-4 flex flex-col gap-2">
      <div className="text-sm font-medium">{t(titleKey)}</div>
      <p className="text-xs text-ink-2">
        {loading
          ? t("integrated.openingPaymentHint")
          : msLeft != null
            ? t(hintKey, { time: formatPixTimeLeft(msLeft) })
            : t(hintNoTimerKey)}
      </p>
      <button
        type="button"
        className="btn-primary w-full mt-1"
        disabled={loading}
        aria-busy={loading}
        onClick={() => void onResume()}
      >
        {loading ? t("integrated.processing") : t(ctaKey)}
      </button>
    </div>
  );
}
